// What the verify screen does with the answer to a correction of the
// destination the visitor typed: pure, so each answer's outcome is tested.

import { z } from "zod";

import {
  correctionFailureOf,
  type CorrectionFailure,
} from "pages/Login/correction-errors";
import type { DestinationChannel } from "pages/Login/destination-edit";
import { correctionProofOf } from "pages/Login/login-grant-flow";
import type { Handoff } from "pages/StartLead/handoff";
import type { LeadResult, SubmitAnswer } from "pages/StartLead/submit";
import type { ResumeNext } from "pages/StartResume/resume";

import type { MintedGrant } from "pages/StartLead/submissionStatus";

/** The body of a correction: exactly one of the corrected phone or email,
 * the handoff that proves the browser when the visitor arrived on the signed
 * link, and the ZIP code from the form when that is the proof. */
export type CorrectionBody = {
  handoff?: Handoff;
  phone?: string;
  email?: string;
  zip?: string;
};

export function correctionBody(
  handoff: Handoff | null,
  channel: DestinationChannel,
  value: string,
  zip: string | null,
): CorrectionBody {
  return {
    ...(handoff === null ? {} : { handoff }),
    ...(channel === "phone" ? { phone: value } : { email: value }),
    ...(zip === null ? {} : { zip }),
  };
}

export type CorrectionOutcome =
  /** The corrected submission needed no confirmation. */
  | { kind: "completed"; next: ResumeNext }
  /** The corrected submission waits on a code for its own grant. */
  | {
      kind: "awaiting";
      submissionId: string;
      grant: MintedGrant;
      /** Binds this browser to the corrected submission; null when the
       * correction was proven by the ZIP code alone. */
      verify: Handoff | null;
    }
  | { kind: "failed"; failure: CorrectionFailure };

const nextSchema = z.object({
  kind: z.string(),
  quote_id: z.string().nullable(),
  case_id: z.string().nullable(),
  event_id: z.string().nullable(),
});

const completedSchema = z.object({
  status: z.literal("completed"),
  next: nextSchema,
});

const awaitingSchema = z.object({
  status: z.literal("awaiting_confirmation"),
  submission_id: z.string().uuid(),
  login_grant: z.string().regex(/^[0-9a-f]{64}$/),
  methods: z.array(z.enum(["sms_otp", "email_link"])).min(1),
  destinations: z.object({
    phone_display: z.string().nullable(),
    email_display: z.string().nullable(),
  }),
  correction_proof: z.unknown().optional().transform(correctionProofOf),
  verify: z
    .object({
      s: z.string(),
      ped: z.string(),
      sig: z.string(),
      exp: z.union([z.string(), z.number()]).transform(String),
    })
    .nullable(),
});

function errorCodeOf(body: unknown): string | null {
  if (typeof body !== "object" || body === null) return null;
  const code = (body as Record<string, unknown>).error_code;
  return typeof code === "string" ? code.toLowerCase() : null;
}

const UNREADABLE: CorrectionFailure = correctionFailureOf(200, null);

/** What an answer to a correction comes to for the screen. */
export function correctionOutcomeOf(answer: SubmitAnswer): CorrectionOutcome {
  if (answer.status === 200) {
    const completed = completedSchema.safeParse(answer.body);
    if (completed.success) {
      return { kind: "completed", next: completed.data.next };
    }
    const awaiting = awaitingSchema.safeParse(answer.body);
    if (awaiting.success) {
      const { destinations, verify } = awaiting.data;
      return {
        kind: "awaiting",
        submissionId: awaiting.data.submission_id,
        grant: {
          grant: awaiting.data.login_grant,
          methods: awaiting.data.methods,
          phoneDisplay: destinations.phone_display,
          emailDisplay: destinations.email_display,
          correctionProof: awaiting.data.correction_proof,
        },
        verify:
          verify === null
            ? null
            : { ped: verify.ped, sig: verify.sig, exp: verify.exp },
      };
    }
    return { kind: "failed", failure: UNREADABLE };
  }
  return {
    kind: "failed",
    failure: correctionFailureOf(answer.status, errorCodeOf(answer.body)),
  };
}

/** The stored result once its submission has been replaced by a corrected
 * one: it follows the new submission, with no grant, so the landing mints a
 * fresh one for it. Null when the stored result is not for the replaced
 * submission, which is then left alone. */
export function resultAfterCorrection(
  result: LeadResult | null,
  replacedId: string,
  newId: string,
): LeadResult | null {
  if (result === null || result.status !== "awaiting_confirmation") return null;
  if (result.submissionId?.toLowerCase() !== replacedId.toLowerCase()) {
    return null;
  }
  return { ...result, submissionId: newId, verification: null };
}
